import React, { useState, useEffect } from 'react';
import { Menu, X, Instagram } from 'lucide-react';
import { salonInfo } from '../data/mock';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';

export const Header = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/${salonInfo.whatsapp.replace(/\s|\+/g, '')}`, '_blank');
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'home', label: t.nav.home },
    { id: 'philosophy', label: t.nav.philosophy },
    { id: 'services', label: t.nav.services },
    { id: 'gallery', label: t.nav.gallery },
    { id: 'booking', label: t.nav.booking },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-neutral-950/85 backdrop-blur-xl border-b border-white/[0.06] py-4'
          : 'bg-neutral-950 py-10 md:py-14'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex justify-between items-center gap-4 md:gap-6">
          <button
            onClick={() => scrollToSection('home')}
            data-testid="header-logo"
            className="flex-shrink-0 group"
            aria-label="Unnie Korean Skincare — Start"
          >
            <img
              src="/assets/logo-brand.png"
              alt="Unnie Korean Skincare Revolution"
              className="h-24 md:h-28 w-auto object-contain transition-opacity duration-500 group-hover:opacity-80"
            />
          </button>

          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`nav-${item.id}`}
                className="eyebrow text-neutral-300 hover:text-lilac transition-colors"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-6">
            <LanguageSwitcher testId="language-switcher-desktop" />
            <a
              href={salonInfo.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-instagram"
              aria-label="Instagram"
              className="text-neutral-300 hover:text-lilac transition-colors"
            >
              <Instagram size={18} strokeWidth={1.5} />
            </a>
            <button
              onClick={openWhatsApp}
              data-testid="header-cta-whatsapp"
              className="eyebrow text-neutral-100 border border-white/20 hover:border-lilac hover:text-lilac px-5 py-2.5 transition-all"
            >
              {t.cta.whatsappShort}
            </button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="md:hidden text-neutral-100"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-neutral-950/95 backdrop-blur-xl border-t border-white/[0.06] mt-4"
        >
          <nav className="px-6 py-6 space-y-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`mobile-nav-${item.id}`}
                className="block w-full text-left eyebrow text-neutral-300 hover:text-lilac py-2"
              >
                {item.label}
              </button>
            ))}
            <div className="pt-4 border-t border-white/10">
              <LanguageSwitcher testId="language-switcher-mobile" />
            </div>
            <a
              href={salonInfo.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-header-instagram"
              className="flex items-center gap-3 eyebrow text-neutral-300 hover:text-lilac py-2"
            >
              <Instagram size={18} strokeWidth={1.5} />
              Instagram
            </a>
            <button
              onClick={openWhatsApp}
              data-testid="mobile-cta-whatsapp"
              className="block w-full text-left eyebrow text-lilac py-2"
            >
              {t.cta.whatsapp}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};
